import { ReactComponent as DotsIcon } from "../../assets/icons/dots.svg";
import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import UIPopover from "../../components/Popover";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../../helpers/api";
import moment from "moment";
import { TableSkeleton } from "../users/Users";
import FullScreenModal from "../../components/FullScreenModal";
import UserIcon from "../../assets/images/user.svg";
import { ShowToast } from "../../components/toast";
import clsx from "clsx";
import { ReactComponent as ArrowIcon } from "../../assets/icons/arrow-down.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/email.svg";
import { ReactComponent as CloudBedsIcon } from "../../assets/icons/cloud-beds.svg";
import { ReactComponent as MewsIcon } from "../../assets/icons/mews.svg";
import { plans } from "../../constant";
import Button from "../../components/Button";
import toast from "react-hot-toast";

type Person = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  joinedOn: string;
  tier: string;
  pms: string;
  lastActive: string;
  profileImg: string;
};

const columnHelper = createColumnHelper<Person>();

export function BlockedUsers({
  isOpen,
  handleClose,
  setSelectedUser,
  setIsUserDetailsOpen,
  refetchActiveUsers,
  onDelete,
}: {
  isOpen: boolean;
  handleClose: () => void;
  setSelectedUser: (value: any) => void;
  setIsUserDetailsOpen: (value: boolean) => void;
  refetchActiveUsers: () => void;
  onDelete: (user: any) => void;
}) {
  const [tableData, setTableData] = useState<Person[] | null>(null);
  const loadMoreRef = useRef<any>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const fetchUsers = async ({ pageParam }) => {
    const res = await api.getUsers({
      page: pageParam,
      limit: 10,
      showActive: false,
    });
    return res;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["blocked-users", isOpen],
    queryFn: fetchUsers,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages, pageParam) => {
      if (lastPage.length < 10) return null;
      return pageParam + 1;
    },
  });

  const columns = [
    columnHelper.accessor("firstName", {
      header: ({ column }) => {
        return (
          <button className="p-0 flex items-center gap-[2px]">Name</button>
        );
      },
      cell: (info) => (
        <div className="flex flex-row items-center gap-2.5">
          <span>
            {info.row.original.firstName} {info.row.original.lastName}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.email, {
      header: "Email",
      cell: (info) => (
        <div className="flex flex-row items-center gap-1">
          <EmailIcon className="w-5 h-5" />
          <span>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor("tier", {
      header: "Plan type",
      cell: (info) =>
        !info.getValue() ? (
          <div>-</div>
        ) : (
          <div
            className={`${
              plans.find((plan) => plan.type === info.getValue())?.planStyle
            } text-xs font-medium leading-3 border rounded py-1 px-1.5 flex items-center justify-center w-fit`}
          >
            {plans.find((plan) => plan.type === info.getValue())?.planName}
          </div>
        ),
    }),
    columnHelper.accessor("pms", {
      header: "PMS",
      cell: (info) => (
        <>
          {info.getValue() === "CLOUDBEDS" ? (
            <CloudBedsIcon className="h-[15px]" />
          ) : info.getValue() === "MEWS" ? (
            <MewsIcon className="h-[15px]" />
          ) : (
            "-"
          )}
        </>
      ),
    }),
    columnHelper.accessor("joinedOn", {
      header: "Joined on",
      cell: (info) => (
        <div className="flex flex-row justify-between w-full items-center">
          {info.getValue()
            ? moment(info.getValue()).format("MMM DD, YYYY") || "-"
            : "-"}
          <UIPopover
            trigger={
              <button className="!rounded-md h-[2.5rem] w-[2.5rem] flex !p-2 items-center justify-center [&_circle]:fill-black [&_circle]:hover:fill-white">
                <DotsIcon />
              </button>
            }
            containerClassName="!z-[50]"
          >
            {(close) => {
              return (
                <div className="flex flex-col text-xs text-black font-medium gap-4 bg-white p-4 rounded-lg shadow-[0px_8px_28px_0px_rgba(20,20,43,0.10)] font-inter mr-16">
                  <button
                    className="text-start"
                    onClick={() => {
                      toast.loading("Activating user...");
                      api
                        .updateUser(info.row.original.id, true)
                        .then(() => {
                          toast.dismiss();
                          close();
                          ShowToast({
                            message: "User activated successfully",
                            type: "success",
                          });
                          refetch();
                          refetchActiveUsers();
                        })
                        .catch((e: any) => {
                          ShowToast({
                            message:
                              e.response.data.message ||
                              "Failed to activate user",
                            type: "error",
                          });
                        });
                    }}
                  >
                    Activate user
                  </button>
                  <button
                    className="text-[#F54343] text-start"
                    onClick={() => onDelete(info.row.original)}
                  >
                    Delete user
                  </button>
                </div>
              );
            }}
          </UIPopover>
        </div>
      ),
    }),
  ];

  const table = useReactTable<Person>({
    data: tableData || [], // Correctly set the data property
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), //client-side sorting
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  useMemo(() => {
    if (data) {
      // Combine pages into one array
      const newData = data.pages.flatMap((page) => page);
      // Set the combined data into the table data state
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 1.0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <FullScreenModal isOpen={isOpen} onClose={handleClose}>
      {/* {isDeleteModalOpen && (
        <ConfirmDelete
          onClose={() => {
            setIsDeleteModalOpen(false);
          }}
          userId={userRowSelected?.id}
          refetch={refetch}
        />
      )} */}
      <div className="overflow-auto h-full relative max-w-[1000px] mx-auto">
        <h1 className="text-2xl font-inter font-medium mb-5 mt-5">
          Deactivated users
        </h1>
        <div className="!h-[calc(100%-120px)] overflow-auto">
          <table className="w-full relative">
            <colgroup>
              <col style={{ width: "27%" }} />
              <col style={{ width: "23%" }} />
              <col style={{ width: "13%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "22%" }} />
            </colgroup>
            <thead className="w-full">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="sticky top-0">
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        "text-[0.875rem] font-medium text-[#202020] text-start p-0"
                      )}
                    >
                      <div
                        className={clsx(
                          "w-full px-5 py-3 bg-[#F4F4F6] flex flex-row items-center gap-1 transition-all",
                          index === headerGroup.headers.length - 1 &&
                            "border-r rounded-tr-[8px] rounded-br-[8px]",
                          index === 0 &&
                            "border-l rounded-tl-[8px] rounded-bl-[8px]"
                        )}
                        // onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {/* {{
                          asc: <ArrowIcon />,
                          desc: <ArrowIcon className="rotate-[180deg]" />,
                        }[header.column.getIsSorted() as string] ?? null} */}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {!isLoading &&
                table.getRowModel().rows.map((row, index) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={clsx(
                          "px-5 py-1 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]",
                          // last index no border
                          index === table.getRowModel().rows.length - 1 &&
                            "border-b-0"
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              {isFetching && (
                <TableSkeleton
                  rows={5}
                  columns={table.getAllColumns().length}
                />
              )}
            </tbody>
          </table>
          {data?.pages?.[0]?.length === 0 && !isFetching && (
            <div className="h-[calc(100%-120px)] flex flex-col justify-center items-center w-full text-sm">
              No deactivated users found
            </div>
          )}
        </div>

        <div className="">
          <button
            onClick={() => fetchNextPage()}
            disabled={!hasNextPage || isFetchingNextPage}
            ref={loadMoreRef}
          >
            {/* {isFetchingNextPage
              ? "Loading more..."
              : hasNextPage
              ? "Load More"
              : "Nothing more to load"} */}
          </button>
        </div>
        {/* <div>{isFetching && !isFetchingNextPage ? "Fetching..." : null}</div> */}
      </div>
    </FullScreenModal>
  );
}
